<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';
import { useLocale } from '../../composables/useLocale';
import useModal, { modals } from '../../composables/useModal';
import { Conflict } from '../Conflict/types';
import CreateUsersTable from './CreateUsersTable.vue';
import { useConflictManager } from '../Conflict/useConflictManager';
import { isGroupLevelRestriction } from '@ilteducation/customer-mgmt-permissions';
import ConflictModal, { CompleteEvent } from '../Conflict/index.vue';
import UploadUsersToCreateModal from './UploadUsersToCreateModal.vue';
import ExistsConflictModel from '../Conflict/ExistsConflictModel.vue';
import DuplicateConflictModel from '../Conflict/DuplicateConflictModel.vue';
import { CreatedUser, useCreateMultipleUsers, UserToCreate } from './useCreateMultipleUsers';

const { t } = useLocale();

const emits = defineEmits<{
  (event: 'usersCreated', value: CreatedUser[]): any;
}>();
const createMultipleUsersTable = ref<typeof CreateUsersTable>();

const { xs } = useDisplay();
const { open: openUploadUserModal } = useModal(modals.UPLOAD_USERS_TO_CREATE);
const { open: openConflictsModal } = useModal(modals.CONFLICT_RESOLVE);
const { open: openResolveDuplicateConflict } = useModal(modals.RESOLVE_DUPLICATE_CONFLICT);
const { open: openResolveExistsConflict } = useModal(modals.RESOLVE_EXISTS_CONFLICT);
const { open: openResolveGroupConflict } = useModal(modals.RESOLVE_INVALID_GROUP_CONFLICT);

const { organization } = useCurrentOrganization();

const { createdUsers, isCreating, progress, isUsersCreated, createUsers, createUser, usersWithError } =
  useCreateMultipleUsers();

onMounted(() => {
  openUploadUserModal();
});

const {
  conflicts,
  resolve,
  resolveSingleConflict,
  validateAll,
  addUsersToValidation,
  isValidating,
  reset: resetConflicts,
  users,
  removeUserByKey,
} = useConflictManager(organization.value);

const areUsersResetable = computed(() => {
  return users.value.length > 0 && !isCreating.value && !isUsersCreated.value;
});

const createAllUsers = async () => {
  if (isCreating.value) {
    return;
  }

  await createUsers(organization.value, users);
  if (usersWithError.value.length === 0) {
    gotoNextStep();
  }
};
const confictModal = ref();
const creatingSingleUser = ref(false);
const createSingleUser = async (user: UserToCreate) => {
  creatingSingleUser.value = true;
  await createUser(organization.value, user);
  creatingSingleUser.value = false;
};

const gotoNextStep = () => {
  emits('usersCreated', createdUsers.value);
};

const resolveConflicts = () => {
  openConflictsModal();
  confictModal.value?.reset();
};

const reset = () => {
  resetConflicts();
  isUsersCreated.value = false;
  isCreating.value = false;
  progress.value = 0;
  createdUsers.value = [];
};

const onRowUpdated = (_index: number) => {
  validateAll();
};

const currentConflict = ref<Conflict | null>(null);
const openResolveConfilctModal = (conflict: Conflict) => {
  currentConflict.value = conflict;
  if (currentConflict.value && currentConflict.value.type === 'duplicate') {
    openResolveDuplicateConflict();
    return;
  }

  if (currentConflict.value && currentConflict.value.type === 'exists') {
    openResolveExistsConflict();
    return;
  }

  if (currentConflict.value && currentConflict.value.type === 'invalid-group') {
    openResolveGroupConflict();
    return;
  }

  console.warn('Unknown conflict type:', currentConflict.value?.type);
};

const resolveCurrentConflict = (value: CompleteEvent) => {
  resolveSingleConflict(value, currentConflict.value as Conflict);
  currentConflict.value = null;
};

// For group-restricted orgs every user must be assigned a group. The per-row picker validates the
// rendered rows, but the virtual table only mounts visible rows, so guard the whole list here too.
const isGroupLevel = computed(() => isGroupLevelRestriction(organization.value));
const hasUserMissingGroup = computed(() => isGroupLevel.value && users.value.some((user) => !user.groupUid));

const isCreateUserButtonDisabled = computed(() => {
  return (
    conflicts.value.length > 0 ||
    !createMultipleUsersTable.value?.isValid ||
    isValidating.value ||
    creatingSingleUser.value ||
    hasUserMissingGroup.value
  );
});

const isResolveConflictsButtonDisabled = computed(() => {
  return conflicts.value.length < 1;
});
</script>

<template>
  <v-row class="ga-6" no-gutters>
    <v-col cols="12">
      <v-form ref="createMultipleUsersTable" name="form">
        <CreateUsersTable
          :items="users"
          :loading="isValidating"
          :readonly="isCreating || isUsersCreated"
          :can-retry-create="isUsersCreated"
          :conflicts="conflicts"
          :organization="organization"
          @retry-create-single-user="createSingleUser"
          @upload-users="openUploadUserModal"
          @remove-user="removeUserByKey"
          @rowUpdated="onRowUpdated"
          @open-resolve-confilct-modal="openResolveConfilctModal"
        />
      </v-form>
    </v-col>

    <v-col cols="12" v-if="isCreating || createMultipleUsersTable?.isValid === false">
      <v-expand-transition>
        <div v-if="isCreating" class="w-100 v-expand-x-transition">
          <v-progress-linear v-model="progress" color="primary" height="8"></v-progress-linear>
        </div>
      </v-expand-transition>
      <v-expand-transition>
        <v-alert
          v-if="createMultipleUsersTable?.isValid === false"
          class="mr-2"
          height="48"
          type="warning"
          variant="outlined"
          border="start"
          icon="mdi-alert"
          density="comfortable"
        >
          {{ t('lcp.fix_errors_before_submitting') }}
        </v-alert>
      </v-expand-transition>
    </v-col>
    <v-col cols="12" class="d-flex flex-wrap w-100 align-center justify-space-between ga-4">
      <v-btn
        :block="xs"
        :disabled="!areUsersResetable"
        variant="outlined"
        prepend-icon="mdi-close"
        @click="reset"
        color="error"
      >
        {{ t('lcp.reset_list') }}
      </v-btn>
      <v-spacer v-if="!xs"></v-spacer>
      <v-btn
        v-if="conflicts.length"
        :block="xs"
        variant="outlined"
        :disabled="isResolveConflictsButtonDisabled"
        append-icon="mdi-chevron-right"
        @click="resolveConflicts"
        class="align-self-right"
      >
        {{ t('lcp.resolve_conflicts', { count: conflicts.length }) }}
      </v-btn>
      <v-btn
        v-if="!isUsersCreated"
        :disabled="isCreateUserButtonDisabled"
        :block="xs"
        variant="flat"
        :loading="isCreating"
        append-icon="mdi-chevron-right"
        @click="createAllUsers"
        class="align-self-right"
      >
        {{ users.length ? t('lcp.create_users_with_count', { count: users.length }) : t('lcp.create_users') }}
      </v-btn>

      <v-btn
        :block="xs"
        v-if="usersWithError.length"
        :disabled="isCreateUserButtonDisabled"
        variant="flat"
        @click="createAllUsers"
        class="align-self-right"
      >
        {{ t('lcp.retry_errors', { count: usersWithError.length }) }}
      </v-btn>

      <v-btn
        :block="xs"
        v-if="isUsersCreated && !isCreating"
        :disabled="isCreateUserButtonDisabled"
        variant="flat"
        append-icon="mdi-chevron-right"
        @click="gotoNextStep"
        class="align-self-right"
      >
        {{
          usersWithError.length > 0 ? t('lcp.ignore_errors_continue', { count: usersWithError.length }) : t('lcp.done')
        }}
      </v-btn>
    </v-col>
  </v-row>
  <UploadUsersToCreateModal
    :name="modals.UPLOAD_USERS_TO_CREATE"
    @users-uploaded="addUsersToValidation"
    :organization="organization"
  />
  <ConflictModal
    ref="confictModal"
    :organizationUid="organization.uid"
    :name="modals.CONFLICT_RESOLVE"
    :conflicted-users="conflicts"
    @complete="resolve"
  />
  <DuplicateConflictModel
    v-if="currentConflict && currentConflict.type === 'duplicate'"
    :name="modals.RESOLVE_DUPLICATE_CONFLICT"
    :conflicts="conflicts"
    :current-conflict="currentConflict"
    @resolve-current-conflict="resolveCurrentConflict"
  />
  <ExistsConflictModel
    v-if="currentConflict && currentConflict.type === 'exists'"
    :name="modals.RESOLVE_EXISTS_CONFLICT"
    :conflicts="conflicts"
    :current-conflict="currentConflict"
    @resolve-current-conflict="resolveCurrentConflict"
  />
</template>
